import React, { useState, useEffect } from "react";
import {
  Button,
  GroupBox,
  Table,
  TableBody,
  TableDataCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TextInput,
  Window,
  WindowContent,
  WindowHeader,
  ProgressBar,
} from 'react95';
import Draggable from 'react-draggable';
import md5 from 'js-md5';
import { t } from './i18n';

function WindowQuote({ items, close, centerWindows, zIndex, isFocused, lang }) {
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [addressName, setAddressName] = useState('');
  const [addressCompany, setAddressCompany] = useState('');
  const [addressStreet1, setAddressStreet1] = useState('');
  const [addressStreet2, setAddressStreet2] = useState('');
  const [addressPostalCode, setAddressPostalCode] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressState, setAddressState] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const [mountTime] = useState(() => new Date().getTime());

  useEffect(() => {
    centerWindows();
    setTimeout(() => {
      centerWindows();
      setOpacity(1);
    }, 150);
    // eslint-disable-next-line
  }, []);

  const sendQuote = () => {
    if (!email || email.indexOf('@') === -1 || email.indexOf('.') === -1) {
      alert(t('quoteAlertEmail', lang));
      return;
    }
    if (!country.trim()) {
      alert(t('quoteAlertCountry', lang));
      return;
    }

    setLoading(true);
    const sendTime = new Date().getTime();
    const itemSkus = items.map(i => i.sku);

    const data = {
      type: 'quote',
      email,
      country,
      items: items.map(i => ({
        sku: i.sku,
        name: i.name,
        brand: i.brand,
        price_ht: i.price_ht,
        price_ttc: i.price_ttc,
      })),
      address: {
        name: addressName,
        company: addressCompany,
        street1: addressStreet1,
        street2: addressStreet2,
        postalCode: addressPostalCode,
        city: addressCity,
        state: addressState,
      },
      lang,
      mountTime,
      sendTime,
      userAgent: navigator.userAgent,
      formTime: sendTime - mountTime,
      checksum: md5(email + itemSkus.join(',') + String(mountTime)),
    };

    const xhr = new XMLHttpRequest();
    xhr.open('POST', 'send.php', true);
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4 && xhr.status === 200) {
        setLoading(false);
        setSent(true);
      } else if (xhr.readyState === 4 && xhr.status !== 200) {
        alert(t('quoteAlertError', lang));
        setLoading(false);
      }
    };
    xhr.send(encodeURI('data=' + JSON.stringify(data)));
  };

  if (sent) {
    return (
      <Draggable bounds="parent" handle=".draggable-title">
        <Window style={{ opacity, zIndex }} className='window center-window dialog-window'>
          <WindowHeader className='window-title'>
            <div className="float-right">
              <Button onClick={close}>&times;</Button>
            </div>
            <div className="draggable-title">
              <span className="Computer_16x16_4 window-icon"></span>
              <span> {t('quoteTitle', lang)}</span>
            </div>
          </WindowHeader>
          <WindowContent>
            <div className="top-16 left-16 right-16">
              <div>
                <div className="User5_32x32_4 transform-2x right-16 inline-block vertical-middle"></div>
                {t('quoteSent', lang)}
              </div>
              &nbsp;
              <p className='align-right top-16'>
                <Button className="buttonLg" primary size='lg' onClick={close}>
                  &nbsp;&nbsp;&nbsp;OK&nbsp;&nbsp;&nbsp;
                </Button>
              </p>
            </div>
          </WindowContent>
        </Window>
      </Draggable>
    );
  }

  return (
    <Draggable bounds="parent" handle=".draggable-title">
      <Window style={{ opacity, zIndex }} className='window center-window quote-window'>
        <WindowHeader className={`window-title${isFocused ? '' : ' inactive'}`}>
          <div className="float-right">
            <Button onClick={close}>&times;</Button>
          </div>
          <div className="draggable-title">
            <span className="Computer_16x16_4 window-icon"></span>
            <span> {t('quoteTitle', lang)}</span>
          </div>
        </WindowHeader>
        <WindowContent>
          <div className="all-8">
            <div className="Computer_32x32_4 transform-2x right-16 inline-block vertical-middle"></div>
            {t('quoteIntro', lang)}
          </div>
          &nbsp;
          <GroupBox label={t('quoteSelectedItems', lang)}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeadCell>{t('shopBrand', lang)}</TableHeadCell>
                  <TableHeadCell>{t('shopModel', lang)}</TableHeadCell>
                  <TableHeadCell>{t('shopPrice', lang)}</TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => (
                  <TableRow key={item.sku}>
                    <TableDataCell style={{ textAlign: 'center' }}>
                      {item.brand === 'Apple'
                        ? <i className="fa-brands fa-apple"></i>
                        : <i className="fa-brands fa-windows"></i>
                      }
                    </TableDataCell>
                    <TableDataCell>{item.name}</TableDataCell>
                    <TableDataCell style={{ whiteSpace: 'nowrap' }}>{item.price_ttc.toFixed(2)} €</TableDataCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </GroupBox>
          &nbsp;
          <GroupBox label={t('quoteContactInfo', lang)}>
            <div className="flex bottom-8">
              <span className="field-label">{t('quoteEmail', lang)}</span>
              <TextInput
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('quoteEmailPlaceholder', lang)}
                type="email"
                fullWidth
                required
                disabled={loading}
              />
            </div>
            <div className="flex bottom-8">
              <span className="field-label">{t('quoteCountry', lang)}</span>
              <TextInput
                value={country}
                onChange={e => setCountry(e.target.value)}
                placeholder={t('quoteCountryPlaceholder', lang)}
                fullWidth
                required
                disabled={loading}
              />
            </div>
          </GroupBox>
          &nbsp;
          <GroupBox label={t('quoteShippingLabel', lang)}>
            <div className="flex bottom-8">
              <span className="field-label">{t('quoteName', lang)}</span>
              <TextInput value={addressName} onChange={e => setAddressName(e.target.value)} fullWidth disabled={loading} />
            </div>
            <div className="flex bottom-8">
              <span className="field-label">{t('quoteCompany', lang)}</span>
              <TextInput value={addressCompany} onChange={e => setAddressCompany(e.target.value)} fullWidth disabled={loading} />
            </div>
            <div className="flex bottom-8">
              <span className="field-label">{t('quoteStreet1', lang)}</span>
              <TextInput value={addressStreet1} onChange={e => setAddressStreet1(e.target.value)} fullWidth disabled={loading} />
            </div>
            <div className="flex bottom-8">
              <span className="field-label">{t('quoteStreet2', lang)}</span>
              <TextInput value={addressStreet2} onChange={e => setAddressStreet2(e.target.value)} fullWidth disabled={loading} />
            </div>
            <div className="flex bottom-8">
              <span className="field-label">{t('quotePostalCode', lang)}</span>
              <TextInput value={addressPostalCode} onChange={e => setAddressPostalCode(e.target.value)} fullWidth disabled={loading} />
            </div>
            <div className="flex bottom-8">
              <span className="field-label">{t('quoteCity', lang)}</span>
              <TextInput value={addressCity} onChange={e => setAddressCity(e.target.value)} fullWidth disabled={loading} />
            </div>
            <div className="flex bottom-8">
              <span className="field-label">{t('quoteState', lang)}</span>
              <TextInput value={addressState} onChange={e => setAddressState(e.target.value)} fullWidth disabled={loading} />
            </div>
          </GroupBox>
          {loading ? (
            <div className="top-8">
              <ProgressBar variant='tile' value={100} />
            </div>
          ) : (
            <p className='align-right top-8'>
              <Button onClick={close} size='lg' className="buttonLg">
                {t('quoteCancel', lang)}
              </Button>&nbsp;
              <Button primary onClick={sendQuote} size='lg' className="buttonLg">
                {t('quoteSend', lang)}
              </Button>
            </p>
          )}
        </WindowContent>
      </Window>
    </Draggable>
  );
}

export default WindowQuote;
